import { jobStateLabel, useNow, type Job } from "../api/jobs";
import type { ExtractionRun, IndexRun, SyncRun } from "../api/types";
import { bytes, duration, elapsed, titleCase } from "../lib/format";
import { Empty, RelativeTime, StatusBadge } from "./ui";

// Run lists share one look: a brand-coloured header bar over separated white
// row-cards (rounded, bordered, a gap apart), matching the pipeline-runs design.
// Numbers are left-aligned like the design, with tabular figures so columns line up.
const GAP = "border-separate border-spacing-x-0 border-spacing-y-2";
const HEAD = "bg-brand px-4 py-3 text-left text-xs font-semibold whitespace-nowrap text-white";
const CELL = "border-y border-gray-100 px-4 py-3 align-middle";

function head(pos: "first" | "mid" | "last") {
  return [HEAD, pos === "first" && "rounded-l-lg", pos === "last" && "rounded-r-lg"].filter(Boolean).join(" ");
}

function cell(pos: "first" | "mid" | "last", selected: boolean) {
  return [
    CELL,
    pos === "first" && "rounded-l-lg border-l",
    pos === "last" && "rounded-r-lg border-r",
    selected ? "bg-brand-light" : "bg-white group-hover:bg-gray-50",
  ].filter(Boolean).join(" ");
}

// A job this browser started whose run row the API hasn't committed yet.
function InProgressRow({ job, colSpan, selected, onSelect }: {
  job: Job;
  colSpan: number;
  selected?: boolean;
  onSelect?: () => void;
}) {
  const now = useNow(true);
  const state = jobStateLabel(job);
  return (
    <tr onClick={onSelect} className={`group ${onSelect ? "cursor-pointer" : ""}`}>
      <td
        colSpan={colSpan}
        className={`${CELL} rounded-lg border-x ${selected ? "bg-brand-light" : "bg-blue-50/70"}`}
      >
        <span className="flex flex-wrap items-center gap-2 text-sm text-blue-900">
          <StatusBadge status={state === "queued" ? "queued" : "running"} label={state} />
          <span className="font-medium">{job.label}</span>
          <span>· {state} for {elapsed(job.startedAt, now)}</span>
          <span className="text-xs text-blue-700">counts appear when the run finishes</span>
        </span>
      </td>
    </tr>
  );
}

function RunError({ error }: { error: string | null }) {
  if (!error) return null;
  return <div className="mt-1 max-w-md truncate text-xs text-red-600" title={error}>{error}</div>;
}

export function SyncRunsTable({ runs, job, selectedId, onSelect, onSelectJob }: {
  runs: SyncRun[];
  job?: Job;
  selectedId?: number | "job" | null;
  onSelect?: (run: SyncRun) => void;
  onSelectJob?: () => void;
}) {
  if (!runs.length && !job) return <Empty>No sync runs yet. Click “Sync Now” to pull courses from Moodle.</Empty>;
  return (
    <table className={`w-full ${GAP}`}>
      <thead>
        <tr>
          <th className={head("first")}>No.</th>
          <th className={head("mid")}>Status</th>
          <th className={head("mid")}>Trigger</th>
          <th className={head("mid")}>Started</th>
          <th className={head("mid")}>Duration</th>
          <th className={head("mid")}>Courses</th>
          <th className={head("mid")}>Created</th>
          <th className={head("mid")}>Updated</th>
          <th className={head("last")}>Deleted</th>
        </tr>
      </thead>
      <tbody>
        {job && <InProgressRow job={job} colSpan={9} selected={selectedId === "job"} onSelect={onSelectJob} />}
        {runs.map((run) => {
          const sel = selectedId === run.id;
          return (
            <tr key={run.id} onClick={onSelect ? () => onSelect(run) : undefined} className={`group ${onSelect ? "cursor-pointer" : ""}`}>
              <td className={`${cell("first", sel)} text-gray-500`}><span className="mr-1 text-gray-400">#</span>{run.id}</td>
              <td className={cell("mid", sel)}><StatusBadge status={run.status} label={titleCase(run.status)} /><RunError error={run.error} /></td>
              <td className={`${cell("mid", sel)} text-gray-600`}>{run.trigger}</td>
              <td className={`${cell("mid", sel)} text-gray-600`}><RelativeTime iso={run.started_at} /></td>
              <td className={`${cell("mid", sel)} tabular-nums text-gray-600`}>{duration(run.started_at, run.finished_at)}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.courses_seen}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.created_count}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.updated_count}</td>
              <td className={`${cell("last", sel)} tabular-nums`}>{run.deleted_count}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function ExtractionRunsTable({ runs, job, selectedId, onSelect }: {
  runs: ExtractionRun[];
  job?: Job;
  selectedId?: number | null;
  onSelect?: (run: ExtractionRun) => void;
}) {
  if (!runs.length && !job) return <Empty>No extraction runs yet.</Empty>;
  return (
    <table className={`w-full ${GAP}`}>
      <thead>
        <tr>
          <th className={head("first")}>No.</th>
          <th className={head("mid")}>Status</th>
          <th className={head("mid")}>Trigger</th>
          <th className={head("mid")}>Started</th>
          <th className={head("mid")}>Duration</th>
          <th className={head("mid")}>Modules</th>
          <th className={head("mid")}>Documents</th>
          <th className={head("mid")}>Assets</th>
          <th className={head("mid")}>Downloaded</th>
          <th className={head("last")}>Failures</th>
        </tr>
      </thead>
      <tbody>
        {job && <InProgressRow job={job} colSpan={10} />}
        {runs.map((run) => {
          const sel = selectedId === run.id;
          return (
            <tr key={run.id} onClick={onSelect ? () => onSelect(run) : undefined} className={`group ${onSelect ? "cursor-pointer" : ""}`}>
              <td className={`${cell("first", sel)} text-gray-500`}><span className="mr-1 text-gray-400">#</span>{run.id}</td>
              <td className={cell("mid", sel)}><StatusBadge status={run.status} label={titleCase(run.status)} /><RunError error={run.error} /></td>
              <td className={`${cell("mid", sel)} text-gray-600`}>{run.trigger}</td>
              <td className={`${cell("mid", sel)} text-gray-600`}><RelativeTime iso={run.started_at} /></td>
              <td className={`${cell("mid", sel)} tabular-nums text-gray-600`}>{duration(run.started_at, run.finished_at)}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.modules_seen}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.documents_written}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.assets_written}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{bytes(run.bytes_downloaded)}</td>
              <td className={`${cell("last", sel)} tabular-nums ${run.failures ? "font-medium text-red-600" : ""}`}>{run.failures}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

export function IndexRunsTable({ runs, job, selectedId, onSelect }: {
  runs: IndexRun[];
  job?: Job;
  selectedId?: number | null;
  onSelect?: (run: IndexRun) => void;
}) {
  if (!runs.length && !job) return <Empty>No index runs yet.</Empty>;
  return (
    <table className={`w-full ${GAP}`}>
      <thead>
        <tr>
          <th className={head("first")}>No.</th>
          <th className={head("mid")}>Status</th>
          <th className={head("mid")}>Trigger</th>
          <th className={head("mid")}>Started</th>
          <th className={head("mid")}>Duration</th>
          <th className={head("mid")}>Documents</th>
          <th className={head("mid")}>Chunks indexed</th>
          <th className={head("mid")}>Chunks deleted</th>
          <th className={head("last")}>Failures</th>
        </tr>
      </thead>
      <tbody>
        {job && <InProgressRow job={job} colSpan={9} />}
        {runs.map((run) => {
          const sel = selectedId === run.id;
          return (
            <tr key={run.id} onClick={onSelect ? () => onSelect(run) : undefined} className={`group ${onSelect ? "cursor-pointer" : ""}`}>
              <td className={`${cell("first", sel)} text-gray-500`}><span className="mr-1 text-gray-400">#</span>{run.id}</td>
              <td className={cell("mid", sel)}><StatusBadge status={run.status} label={titleCase(run.status)} /><RunError error={run.error} /></td>
              <td className={`${cell("mid", sel)} text-gray-600`}>{run.trigger}</td>
              <td className={`${cell("mid", sel)} text-gray-600`}><RelativeTime iso={run.started_at} /></td>
              <td className={`${cell("mid", sel)} tabular-nums text-gray-600`}>{duration(run.started_at, run.finished_at)}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.documents_seen}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.chunks_indexed}</td>
              <td className={`${cell("mid", sel)} tabular-nums`}>{run.chunks_deleted}</td>
              <td className={`${cell("last", sel)} tabular-nums ${run.failures ? "font-medium text-red-600" : ""}`}>{run.failures}</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
